// Third party library imports
import { I18nPluralPipe, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { finalize, Subject, takeUntil, takeWhile, tap, timer } from 'rxjs';

// Zopup library imports
import { StorageService } from '@zopup/zop-shared';
import { ZopBrandingService } from '@zopup/zop-ux-core';

// Internal project imports
import { ZopAuthConfigService } from '../../providers/zop-auth-config.provider';

@Component({
    selector: 'zop-auth-sign-out',
    templateUrl: './sign-out.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, RouterLink, I18nPluralPipe]
})
export class AuthSignOutComponent implements OnInit, OnDestroy {
    countdown: number = 5;
    countdownMapping: any = {
        '=1': '# second',
        other: '# seconds',
    };

    options: AnimationOptions = {
        path: '/assets/animation/zopup-dark.json',
        autoplay: true,
        loop: false,
    };

    private redirectUrl: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    readonly authConfig = inject(ZopAuthConfigService);
    readonly branding = inject(ZopBrandingService);

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _storageService: StorageService,
        private _router: Router
    ) {
        this.redirectUrl =
            this._activatedRoute.snapshot.queryParamMap.get('redirectURL');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Clear the local storage only if the redirect URL is not set.
        // As of today i.e., 19 Aug 24, the 'redirectUrl' will be present only if auth token is expired. (see 'auth.guard.ts')
        // To preserve the user's data when their token expires and they're redirected to the sign-in page, we won't clear the storage if a redirect URL is provided. This ensures that the data can be used again after the user successfully signs in.
        if (!this.redirectUrl) {
            this._storageService.clearStorage();
        }

        // Redirect after the countdown
        timer(1000, 1000)
            .pipe(
                finalize(() => {
                    this._router.navigate([this.authConfig.getConfig().routes.signIn]);
                }),
                takeWhile(() => this.countdown > 0),
                takeUntil(this._unsubscribeAll),
                tap(() => this.countdown--)
            )
            .subscribe();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
